/**
 * ledgerPeriodOfKey
 *
 * The fiscal period a `ledgerPeriodKey` names, or null when the key is malformed or is not the
 * start date of any period, such as a mid-period date. The key's date is read at the end of its local
 * day, so a grid anchored after midnight (06:00) still resolves to the period starting that day.
 * Consumers: `ledgerClosePeriod` and `ledgerReopenPeriod`, which refuse a key that names no period.
 */

import { DateTime } from 'luxon';
import type { LedgerCalendarStructure } from '../System.Ledger.Definition.types';
import type { LedgerPeriod } from './LedgerPeriod.types';
import { ledgerPeriodKey } from './ledgerPeriodKey';
import { ledgerPeriodOf } from './ledgerPeriodOf';

const PERIOD_KEY = /^\d{4}-\d{2}-\d{2}$/;

function ledgerPeriodOfKey(periodKey: string, structure: LedgerCalendarStructure): LedgerPeriod | null {
    if (!PERIOD_KEY.test(periodKey)) return null;
    const day = DateTime.fromISO(periodKey, { zone: structure.timeZone });
    if (!day.isValid) return null;
    const period = ledgerPeriodOf(day.endOf('day'), structure);
    return ledgerPeriodKey(period) === periodKey ? period : null;
}

export { ledgerPeriodOfKey };
